import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Carrinho } from '@app/data/carrinho/carrinho.model';
import TIPO_ACESSO_ENDERECO from '@app/shared/consts/tipo-acesso-endereco.const';
import { CepService, CUSTOM_MASKS } from '@app/shared/services';
import { BaseService } from '@app/shared/services/base';
import { Subscription } from 'rxjs';
import { CarrinhoService } from './carrinho.service';
import { AuthService } from '@app/auth';

type FormTransporte = {
  bairro: FormControl<string>;
  cep: FormControl<string>;
  cidade: FormControl<string>;
  complemento: FormControl<string>;
  contatoResponsavel: FormControl<string>;
  estado: FormControl<string>;
  logradouro: FormControl<string>;
  numero: FormControl<string>;
  obs: FormControl<string>;
  periodo: FormControl<string>;
  responsavel: FormControl<string>;
  status: FormControl<string>;
  taxaInterna: FormControl<number>;
  taxaPagamento: FormControl<number>;
  tipoAcesso: FormControl<string>;
  valorPagoAoPrestador: FormControl<number>;
  valorPagoPeloCliente: FormControl<number>;
};

type FormCliente = {
  nome: FormControl<string>;
  email: FormControl<string>;
  celular: FormControl<string>;
};

@Component({
  selector: 'app-carrinho',
  templateUrl: './carrinho.component.html',
  styleUrls: ['./carrinho.component.scss'],
})
export class CarrinhoComponent implements OnInit {
  public carrinho: Carrinho;
  public masks = CUSTOM_MASKS.masks;
  public vendaB2B: boolean = false;
  public TIPOACESSO = TIPO_ACESSO_ENDERECO;

  public formCliente: FormGroup<FormCliente> = new FormGroup<FormCliente>({
    nome: new FormControl<string>(null, [Validators.required]),
    email: new FormControl<string>(null, [Validators.required]),
    celular: new FormControl<string>(null, [Validators.required]),
  });

  public formTransporte: FormGroup<FormTransporte> = new FormGroup<FormTransporte>({
    bairro: new FormControl({ value: null, disabled: true }),
    cep: new FormControl(null, [Validators.required]),
    cidade: new FormControl({ value: null, disabled: true }),
    complemento: new FormControl(null),
    contatoResponsavel: new FormControl(null),
    estado: new FormControl({ value: null, disabled: true }),
    logradouro: new FormControl({ value: null, disabled: true }),
    numero: new FormControl(null, [Validators.required]),
    obs: new FormControl(null),
    periodo: new FormControl(null),
    responsavel: new FormControl(null),
    status: new FormControl('Pendente'),
    taxaInterna: new FormControl(0),
    taxaPagamento: new FormControl(0),
    tipoAcesso: new FormControl(null, [Validators.required]),
    valorPagoAoPrestador: new FormControl(null),
    valorPagoPeloCliente: new FormControl(null),
  });

  private watchForm: Subscription = new Subscription();

  constructor(
    private _carrinhoService: CarrinhoService,
    private _base: BaseService,
    private _changeDetector: ChangeDetectorRef,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _cepService: CepService,
    private _authService: AuthService
  ) { }

  get desabilitarRetiradaNaLoja() {
    return this.carrinho.itens.some(({ produto }) => produto.dropShipping);
  }

  get desabilitarProsseguirParaPagamento(): boolean {
    return this.formCliente.invalid || (this.formTransporte.invalid && !this.carrinho.retirarNaLoja);
  }

  ngOnInit(): void {
    this.getCarrinho();
    this._getRouteParams();
    this._initWatcherForm();

    this._base._titleService.setTitle('Carrinho');
  }

  ngOnDestroy(): void {
    this.watchForm.unsubscribe();
  }

  private _initWatcherForm() {
    this.watchForm = this.formTransporte.controls.cep.valueChanges.subscribe(() => {
      this.buscarCep();
    });
  }

  private _getRouteParams(): void {
    this.vendaB2B = this._activatedRoute.snapshot.queryParams.vendaB2B == 'true';
  }

  public getCarrinho(): void {
    this._base._loading.start();

    this._carrinhoService.atualizarCarrinho();

    this._carrinhoService.carrinho
      .subscribe((update) => {
        this.carrinho = update;

        this._base._loading.stop();
        this._changeDetector.detectChanges();

        this._carregarEnderecoLocal();
        this._preencherDadosUsuario();

        if (update) this._base.analyticsEvents.visualizarCarrinho(update, update.itens);
      })
      .add(() => this._base._loading.stop());
  }

  private _carregarEnderecoLocal(): void {
    this.formTransporte.patchValue(JSON.parse(localStorage.getItem('endereco')));
  }

  private _preencherDadosUsuario(): void {
    if (!!this.carrinho?.usuario) {
      this.formCliente.patchValue({
        nome: this.carrinho.usuario.nome,
        email: this.carrinho.usuario.email,
        celular: this.carrinho.usuario.celular,
      });
    } else if (!!this._authService.usuario) {
      const usuario = this._authService.usuario;

      this.formCliente.patchValue({
        nome: usuario.nome,
        email: usuario.email,
        celular: usuario.celular,
      });
    }
  }

  public toggleFrete(): void {
    this._base._loading.start();
    this._carrinhoService
      .toggleFrete()
      .subscribe(
        () => this._carrinhoService.atualizarCarrinho(),
        (err) => this._base._alert.error('Atenção', err)
      )
      .add(() => this._base._loading.start());
  }

  public finalizarVenda() {
    this._base._loading.start();

    this._atualizarEnderecoLocal();

    if (!this.carrinho.retirarNaLoja) {
      if (this.formTransporte.invalid) {
        this._base._alert.error('Atenção', 'Endereço inválido');
        return;
      }
    }

    this._carrinhoService
      .transformarCarrinhoEmVenda(this.formCliente.getRawValue(), this.vendaB2B, this.formTransporte.getRawValue())
      .subscribe(
        async ({ data }) => {
          this._base._toast.success('Seu pedido foi enviado com sucesso!');
          this._base.analyticsEvents.iniciarCheckout(this.carrinho, this.carrinho.itens);

          // if (data?.mutation?.invoices?.length > 0) {
          //   this._router.navigate(['/invoice', data.mutation.invoices[0].id]);
          // } else {
          //   this._router.navigateByUrl('/login');
          // }
        },
        (err) => {
          this._base._alert.error('Atenção', err);
        }
      )
      .add(() => {
        this._base._loading.stop()
        this._base._alert.success('Seu pedido foi enviado com sucesso!', 'Um vendedor entrará em contato com você em breve.');
        this._router.navigateByUrl('/');
      });
  }

  private _atualizarEnderecoLocal(): void {
    if (!this.carrinho.retirarNaLoja)
      localStorage.setItem('endereco', JSON.stringify({ ...JSON.parse(localStorage.getItem('endereco')), ...this.formTransporte.getRawValue() }));
  }

  public buscarCep(): void {
    const cep = this.formTransporte.controls.cep.value?.replace(new RegExp('-', 'g'), '').replace(new RegExp('_', 'g'), '');

    if (cep?.length === 8) {
      this._base._loading.start();

      this._cepService.consultarDistancia(this.formTransporte.controls.cep.value).subscribe(({ data }) => {
        if (data.consultarDistancia.distancia_km > 50) {
          this.formTransporte.reset();
          this._base._alert.error('Atenção', 'Endereço inválido');
          this._base._loading.stop();

          return;
        }

        const enderecoComDistancia = data.consultarDistancia;

        localStorage.setItem('endereco', JSON.stringify(enderecoComDistancia));

        this._base._loading.stop();

        this.formTransporte.patchValue({ ...data.consultarDistancia }, { emitEvent: false });
      });
    }
  }
}
